import type { FeatureCollection } from 'geojson';
import { type PositionAnchor } from 'maplibre-gl';
import type { DataSourceConfig } from '@/components/maplibre';
import { ColumnMetadata, LayerStats } from '@/components/maplibre/types';
import { getLayerLegendColors } from '@/components/maplibre/utils/maplibre-utils';
import { LAYER_GEOMETRY_TYPE } from '@/lib/db-constants';
import { Layer, LayerGeometryType } from '@/lib/db-row-types';
import { type BorderType, DASH_ARRAY_PATTERNS } from '@/utils/layer-sources/style-borders';
import {
  DEFAULT_STYLE_VALUES,
  LABEL_MODE,
  type LabelFontStyle,
  LabelModeValue,
  STYLE_MODE,
  STYLE_PROPERTY_DIRECTION,
  StylePropertyDirection,
} from '@/utils/layer-sources/style-constants';
import {
  ColorMode,
  CustomColorsConfig,
  LabelStyle,
  LabelText,
  NumericMode,
  type StyleV2,
  isStyleV2,
} from '@/utils/layer-sources/style-v2';

/**
 * Configuration for property analysis
 */
const ANALYSIS_CONFIG = {
  SAMPLE_SIZE: 100, // Number of features to sample for analysis
  NUMERIC_THRESHOLD: 0.8, // 80% of values must be numeric for property to be considered numeric
  MIN_UNIQUE_VALUES: 1, // Minimum unique values required for meaningful styling
  MAX_CATEGORY_VALUES: Infinity, // Maximum unique values for categorical styling
} as const;

const INITIAL_VALIDATION_RESULT: PropertyValidation = {
  category: [],
  value: [],
  invalid: [],
  analysis: {},
};

/**
 * Property analysis results
 */
export interface PropertyAnalysis {
  isNumeric: boolean;
  hasValidValues: boolean;
  uniqueValueCount: number;
  sampleValues: (string | number)[];
  nullCount?: number;
  totalCount: number;
}

export interface PropertyItem {
  columnId: string | number | null;
  columnName: string;
  /**
   * Original (pre-sanitization) label to show in the UI. Falls back to
   * `columnName` when the column was never renamed. `columnName` stays the
   * canonical identifier used in style expressions / tile property lookups.
   */
  displayName?: string;
}

/**
 * Property validation results
 */
export interface PropertyValidation {
  category: PropertyItem[];
  value: PropertyItem[];
  invalid: PropertyItem[];
  analysis: Record<string, PropertyAnalysis>;
}

/**
 * Checks if a value can be converted to a valid number
 */
function isValidNumber(value: any): boolean {
  if (value === null || value === undefined || value === '') return false;
  const num = Number(value);
  if (!isNaN(num) && isFinite(num)) {
    return true;
  }
  return false;
}

/**
 * Checks if a value is considered "empty" or invalid for styling
 */
function isEmptyValue(value: any): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'string' && value.trim() === '')
  );
}

/**
 * Analyzes a single property across all features to determine its characteristics
 */
export function analyzeProperty(features: any[], propertyName: string): PropertyAnalysis {
  const sampled = features.slice(0, ANALYSIS_CONFIG.SAMPLE_SIZE);
  const totalCount = sampled.length;

  const values = new Set<string | number>();
  const numericValues: number[] = [];
  let validValueCount = 0;
  let nullCount = 0;

  for (const feature of sampled) {
    const value = feature.properties?.[propertyName];

    if (isEmptyValue(value)) {
      nullCount++;
      continue;
    }

    validValueCount++;
    values.add(value);

    if (isValidNumber(value)) {
      numericValues.push(Number(value));
    }
  }

  const numericRatio = validValueCount > 0 ? numericValues.length / validValueCount : 0;
  const isNumeric = numericRatio >= ANALYSIS_CONFIG.NUMERIC_THRESHOLD;

  return {
    isNumeric,
    hasValidValues: validValueCount > 0,
    uniqueValueCount: values.size,
    sampleValues: Array.from(values).slice(0, 10),
    nullCount,
    totalCount,
  };
}

/**
 * Analyzes a single property across tileset featureData to determine its characteristics
 */
function analyzeTilesetProperty(
  featureData: Record<string, any>,
  propertyName: string
): PropertyAnalysis {
  const propertyValues = Object.values(featureData);
  const sampled = propertyValues.slice(0, ANALYSIS_CONFIG.SAMPLE_SIZE);
  const totalCount = sampled.length;

  const values = new Set<string | number>();
  const numericValues: number[] = [];
  let validValueCount = 0;
  let nullCount = 0;

  for (const properties of sampled) {
    const value = properties?.[propertyName];

    if (isEmptyValue(value)) {
      nullCount++;
      continue;
    }

    validValueCount++;
    values.add(value);

    if (isValidNumber(value)) {
      numericValues.push(Number(value));
    }
  }

  const numericRatio = validValueCount > 0 ? numericValues.length / validValueCount : 0;
  const isNumeric = numericRatio >= ANALYSIS_CONFIG.NUMERIC_THRESHOLD;

  return {
    isNumeric,
    hasValidValues: validValueCount > 0,
    uniqueValueCount: values.size,
    sampleValues: Array.from(values).slice(0, 10),
    nullCount,
    totalCount,
  };
}

/**
 * Validates whether a property is suitable for categorical styling
 */
function isValidForCategorical(analysis: PropertyAnalysis): boolean {
  return analysis.hasValidValues && analysis.uniqueValueCount >= ANALYSIS_CONFIG.MIN_UNIQUE_VALUES;
}

/**
 * Validates whether a property is suitable for value-based styling
 */
function isValidForValue(analysis: PropertyAnalysis): boolean {
  return (
    analysis.isNumeric &&
    analysis.hasValidValues &&
    analysis.uniqueValueCount >= ANALYSIS_CONFIG.MIN_UNIQUE_VALUES
  );
}

function propertyNameToItem(propertyName: string, columnMetadata?: ColumnMetadata[]): PropertyItem {
  const metadata = columnMetadata?.find(col => col.column_name === propertyName);
  return {
    columnId: metadata?.column_id ?? null,
    columnName: propertyName,
    displayName: metadata?.display_name ?? propertyName,
  };
}

function propertyNamesToItems(
  propertyNames: string[],
  columnMetadata?: ColumnMetadata[]
): PropertyItem[] {
  // Alphabetical by the label the user actually sees, so every column dropdown
  // in the styling panel is scannable regardless of stats/tile column order.
  return propertyNames
    .map(name => propertyNameToItem(name, columnMetadata))
    .sort((a, b) =>
      (a.displayName ?? a.columnName).localeCompare(b.displayName ?? b.columnName, undefined, {
        sensitivity: 'base',
        numeric: true,
      })
    );
}

/**
 * Analyzes all properties in a GeoJSON FeatureCollection and categorizes them for styling
 */
export function validatePropertiesForStyling(
  geoJsonData: FeatureCollection,
  columnMetadata?: ColumnMetadata[]
): PropertyValidation {
  const features = geoJsonData.features || [];

  if (features.length === 0) {
    return INITIAL_VALIDATION_RESULT;
  }

  const firstFeature = features[0];
  const allPropertyNames = Object.keys(firstFeature?.properties || {});

  const categoryProperties: string[] = [];
  const valueProperties: string[] = [];
  const invalidProperties: string[] = [];
  const analysisResults: Record<string, PropertyAnalysis> = {};

  for (const propertyName of allPropertyNames) {
    const analysis = analyzeProperty(features, propertyName);
    analysisResults[propertyName] = analysis;

    const validForValue = isValidForValue(analysis);
    const validForCategorical = isValidForCategorical(analysis);

    if (validForValue) {
      valueProperties.push(propertyName);
    }

    if (validForCategorical) {
      categoryProperties.push(propertyName);
    }

    if (!validForValue && !validForCategorical) {
      invalidProperties.push(propertyName);
    }
  }

  return {
    category: propertyNamesToItems(categoryProperties, columnMetadata),
    value: propertyNamesToItems(valueProperties, columnMetadata),
    invalid: propertyNamesToItems(invalidProperties, columnMetadata),
    analysis: analysisResults,
  };
}

/**
 * Analyzes all properties in a tileset's featureData and categorizes them for styling
 */
export function validateTilesetPropertiesForStyling(
  featureData: Record<string, any>,
  columnMetadata?: ColumnMetadata[]
): PropertyValidation {
  if (!featureData || Object.keys(featureData).length === 0) {
    return INITIAL_VALIDATION_RESULT;
  }

  const allPropertyNames = new Set<string>();
  Object.values(featureData).forEach(properties => {
    if (properties && typeof properties === 'object') {
      Object.keys(properties).forEach(prop => allPropertyNames.add(prop));
    }
  });

  if (allPropertyNames.size === 0) {
    return INITIAL_VALIDATION_RESULT;
  }

  const categoryProperties: string[] = [];
  const valueProperties: string[] = [];
  const invalidProperties: string[] = [];
  const analysisResults: Record<string, PropertyAnalysis> = {};

  for (const propertyName of Array.from(allPropertyNames)) {
    const analysis = analyzeTilesetProperty(featureData, propertyName);
    analysisResults[propertyName] = analysis;

    const validForValue = isValidForValue(analysis);
    const validForCategorical = isValidForCategorical(analysis);

    if (validForValue) {
      valueProperties.push(propertyName);
    }

    if (validForCategorical) {
      categoryProperties.push(propertyName);
    }

    if (!validForValue && !validForCategorical) {
      invalidProperties.push(propertyName);
    }
  }

  return {
    category: propertyNamesToItems(categoryProperties, columnMetadata),
    value: propertyNamesToItems(valueProperties, columnMetadata),
    invalid: propertyNamesToItems(invalidProperties, columnMetadata),
    analysis: analysisResults,
  };
}

export function validateReadonlyTilesetPropertiesForStyling(
  stats: LayerStats,
  columnMetadata?: ColumnMetadata[]
): PropertyValidation {
  if (!stats?.fields) {
    return INITIAL_VALIDATION_RESULT;
  }

  const categoryProperties: string[] = [];
  const valueProperties: string[] = [];
  const invalidProperties: string[] = [];
  const analysisResults: Record<string, PropertyAnalysis> = {};

  for (const field of stats.fields) {
    if (field.type === 'unknown') {
      invalidProperties.push(field.name);
      continue;
    }

    const isNumeric = field.type === 'number';
    const uniqueValueCount = field.count_distinct;
    const totalCount = field.count;

    const sampleValues: (string | number)[] = [];
    if (field.values && typeof field.values === 'object') {
      sampleValues.push(...Object.keys(field.values).slice(0, 10));
    }

    const analysis: PropertyAnalysis = {
      isNumeric,
      hasValidValues: totalCount > 0,
      uniqueValueCount,
      sampleValues,
      totalCount,
    };

    analysisResults[field.name] = analysis;

    const validForValue = isNumeric && field.min !== field.max;

    if (validForValue) {
      valueProperties.push(field.name);
    }

    categoryProperties.push(field.name);
  }

  return {
    category: propertyNamesToItems(categoryProperties, columnMetadata),
    value: propertyNamesToItems(valueProperties, columnMetadata),
    invalid: propertyNamesToItems(invalidProperties, columnMetadata),
    analysis: analysisResults,
  };
}

// Helper function to ensure color values are always stored in uppercase
export function normalizeColor(color: string): string {
  return color.toUpperCase();
}

const toDecimal = (percentage: number): number => percentage / 100;
const toPercentage = (decimal: number): number => decimal * 100;

export interface StyleState {
  iconId: string | null;
  fillColor: string;
  fillOpacity: number;
  polygonFillColor: string;
  lineColor: string;
  // Point/Circle size - direct style value for fixed size
  size: number;
  strokeColor: string;
  strokeWidth: number;
  strokeOpacity: number;
  borderType: BorderType;
  opacity: number;
  polygonFillOpacity: number;
  lineWidth: number;
  lineOpacity: number;
  colorLogicColumn: string;
  colorLogicScheme: string | null;
  colorLogicType: 'category' | 'value';
  colorLogicEnabled: boolean;
  colorLogicDataMin?: number;
  colorLogicDataMax?: number;
  customColors?: CustomColorsConfig[] | null;
  polygonStrokeColor: string;
  polygonStrokeWidth: number;
  polygonStrokeOpacity: number;
  sizeLogic: {
    sizeBy: string | null; // null means fixed size
    minSize: number;
    maxSize: number;
    direction: StylePropertyDirection;
  };
  labelLogic: {
    labelBy: string | null;
    labelText?: LabelText;
    size: number;
    color: string;
    haloColor: string;
    haloWidth: number;
    fontStyle: LabelFontStyle;
    textVariableAnchor?: PositionAnchor;
    textRadialOffset?: number;
  };
}

export function getLayerRepresentativeColor(
  geometryTypes: Set<LayerGeometryType>,
  styleState: Pick<StyleState, 'fillColor' | 'polygonFillColor' | 'lineColor'>,
  hasLines: boolean = false
): string {
  if (geometryTypes.has(LAYER_GEOMETRY_TYPE.POINT)) {
    return styleState.fillColor;
  }
  if (geometryTypes.has(LAYER_GEOMETRY_TYPE.POLYGON)) {
    return styleState.polygonFillColor;
  }
  if (hasLines) {
    return styleState.lineColor;
  }
  return '#2DD4BF';
}

export type GeometryTypeLabel = 'Polygon' | 'Lines' | 'Points';

export function getGeometryTypeLabel(geometryTypes: Set<LayerGeometryType>): GeometryTypeLabel {
  if (geometryTypes.has(LAYER_GEOMETRY_TYPE.POLYGON)) return 'Polygon';
  if (geometryTypes.has(LAYER_GEOMETRY_TYPE.LINE_STRING)) return 'Lines';
  return 'Points';
}

/**
 * Analyze geometry types to determine presence of polygons and lines and whether the layer is lines-only.
 * Useful because polygon layers also require line layers for borders, while a pure line_string layer should be treated as lines-only.
 */
export function analyzeGeometryTypes(
  geometryTypes?: readonly LayerGeometryType[] | Set<LayerGeometryType>
): {
  hasPolygon: boolean;
  hasLine: boolean;
  hasPoint: boolean;
  isLinesOnly: boolean;
  isPointsOnly: boolean;
} {
  const typesSet: Set<LayerGeometryType> = geometryTypes
    ? geometryTypes instanceof Set
      ? geometryTypes
      : new Set(geometryTypes)
    : new Set();

  const hasPolygon = typesSet.has(LAYER_GEOMETRY_TYPE.POLYGON);
  const hasLine = typesSet.has(LAYER_GEOMETRY_TYPE.LINE_STRING);
  const hasPoint = typesSet.has(LAYER_GEOMETRY_TYPE.POINT);
  const isLinesOnly = hasLine && !hasPolygon && !hasPoint;
  const isPointsOnly = hasPoint && !hasLine && !hasPolygon;

  return { hasPolygon, hasLine, isLinesOnly, isPointsOnly, hasPoint };
}

/**
 * Converts v2 StyleV2 to deprecated StyleState (UI format) for backwards compatibility
 */
function parseV2StyleData(v2Style: StyleV2): StyleState {
  const result: StyleState = { ...DEFAULT_STYLE_VALUES };

  const getStaticValue = <T>(mode: { mode: string; value?: T } | undefined, defaultValue: T): T => {
    if (mode?.mode === STYLE_MODE.FIXED && mode.value !== undefined) {
      return mode.value;
    }
    return defaultValue;
  };

  const getColumnId = (mode: ColorMode | NumericMode): string => {
    if (mode && mode.mode !== STYLE_MODE.FIXED && mode.columnId) {
      return String(mode.columnId);
    }
    return '';
  };

  const getColorScheme = (mode: ColorMode): string | null => {
    if (mode && mode.mode !== STYLE_MODE.FIXED && mode.scheme) {
      if (mode.mode === STYLE_MODE.CATEGORY && mode.customColors) {
        return null;
      }

      return mode.scheme;
    }
    return '';
  };

  if (v2Style.polygon) {
    result.polygonFillColor = getStaticValue(
      v2Style.polygon.fill.color.mode === STYLE_MODE.FIXED ? v2Style.polygon.fill.color : undefined,
      DEFAULT_STYLE_VALUES.polygonFillColor
    );
    result.polygonFillOpacity = toPercentage(
      getStaticValue(
        v2Style.polygon.fill.opacity.mode === STYLE_MODE.FIXED
          ? v2Style.polygon.fill.opacity
          : undefined,
        toDecimal(DEFAULT_STYLE_VALUES.polygonFillOpacity)
      )
    );

    result.polygonStrokeColor = getStaticValue(
      v2Style.polygon.border.color.mode === STYLE_MODE.FIXED
        ? v2Style.polygon.border.color
        : undefined,
      DEFAULT_STYLE_VALUES.polygonStrokeColor
    );
    result.polygonStrokeOpacity = toPercentage(
      getStaticValue(
        v2Style.polygon.border.opacity.mode === STYLE_MODE.FIXED
          ? v2Style.polygon.border.opacity
          : undefined,
        toDecimal(DEFAULT_STYLE_VALUES.polygonStrokeOpacity)
      )
    );
    result.polygonStrokeWidth = getStaticValue(
      v2Style.polygon.border.width.mode === STYLE_MODE.FIXED
        ? v2Style.polygon.border.width
        : undefined,
      DEFAULT_STYLE_VALUES.polygonStrokeWidth
    );
    result.borderType = (v2Style.polygon.border.borderType || 'solid') as BorderType;

    if (v2Style.polygon.fill.color.mode !== STYLE_MODE.FIXED) {
      result.colorLogicEnabled = true;
      result.colorLogicColumn = getColumnId(v2Style.polygon.fill.color);
      result.colorLogicScheme = getColorScheme(v2Style.polygon.fill.color);
      result.colorLogicType = v2Style.polygon.fill.color.mode === 'category' ? 'category' : 'value';
      if (v2Style.polygon.fill.color.mode === STYLE_MODE.RANGE) {
        result.colorLogicDataMin = v2Style.polygon.fill.color.dataMin;
        result.colorLogicDataMax = v2Style.polygon.fill.color.dataMax;
      }

      if (v2Style.polygon.fill.color.mode === STYLE_MODE.CATEGORY) {
        result.customColors = v2Style.polygon.fill.color.customColors;
      }
    }
  }

  if (v2Style.line) {
    result.lineColor = getStaticValue(
      v2Style.line.color?.mode === STYLE_MODE.FIXED ? v2Style.line.color : undefined,
      DEFAULT_STYLE_VALUES.lineColor
    );
    result.lineOpacity = toPercentage(
      getStaticValue(
        v2Style.line.opacity?.mode === STYLE_MODE.FIXED ? v2Style.line.opacity : undefined,
        toDecimal(DEFAULT_STYLE_VALUES.lineOpacity)
      )
    );
    result.lineWidth = getStaticValue(
      v2Style.line.width?.mode === STYLE_MODE.FIXED ? v2Style.line.width : undefined,
      DEFAULT_STYLE_VALUES.lineWidth
    );
    result.borderType = (v2Style.line.borderType || 'solid') as BorderType;

    if (!result.colorLogicEnabled && v2Style.line.color?.mode !== STYLE_MODE.FIXED) {
      result.colorLogicEnabled = true;
      result.colorLogicColumn = getColumnId(v2Style.line.color);
      result.colorLogicScheme = getColorScheme(v2Style.line.color);
      result.colorLogicType = v2Style.line.color.mode === 'category' ? 'category' : 'value';
      if (v2Style.line.color.mode === STYLE_MODE.RANGE) {
        result.colorLogicDataMin = v2Style.line.color.dataMin;
        result.colorLogicDataMax = v2Style.line.color.dataMax;
      }

      if (v2Style.line.color.mode === STYLE_MODE.CATEGORY) {
        result.customColors = v2Style.line.color.customColors;
      }
    }
  }

  if (v2Style.point) {
    result.size = getStaticValue(
      v2Style.point.size?.mode === STYLE_MODE.FIXED ? v2Style.point.size : undefined,
      DEFAULT_STYLE_VALUES.size
    );
    result.fillColor = getStaticValue(
      v2Style.point.color?.mode === STYLE_MODE.FIXED ? v2Style.point.color : undefined,
      DEFAULT_STYLE_VALUES.fillColor
    );
    result.fillOpacity = toPercentage(
      getStaticValue(
        v2Style.point.opacity?.mode === STYLE_MODE.FIXED ? v2Style.point.opacity : undefined,
        toDecimal(DEFAULT_STYLE_VALUES.fillOpacity)
      )
    );
    result.strokeColor = getStaticValue(
      v2Style.point.strokeColor?.mode === STYLE_MODE.FIXED ? v2Style.point.strokeColor : undefined,
      DEFAULT_STYLE_VALUES.strokeColor
    );
    result.strokeOpacity = toPercentage(
      getStaticValue(
        v2Style.point.strokeOpacity?.mode === STYLE_MODE.FIXED
          ? v2Style.point.strokeOpacity
          : undefined,
        toDecimal(DEFAULT_STYLE_VALUES.strokeOpacity)
      )
    );
    result.strokeWidth = getStaticValue(
      v2Style.point.strokeWidth?.mode === STYLE_MODE.FIXED ? v2Style.point.strokeWidth : undefined,
      DEFAULT_STYLE_VALUES.strokeWidth
    );

    result.iconId =
      v2Style.point.icon?.mode === STYLE_MODE.FIXED && v2Style.point.icon?.value
        ? v2Style.point.icon.value
        : DEFAULT_STYLE_VALUES.iconId;

    if (v2Style.point.size?.mode === STYLE_MODE.RANGE) {
      result.sizeLogic = {
        sizeBy: getColumnId(v2Style.point.size),
        minSize: v2Style.point.size.min,
        maxSize: v2Style.point.size.max,
        direction: v2Style.point.size.direction ?? STYLE_PROPERTY_DIRECTION.ASCENDING,
      };
    }

    if (!result.colorLogicEnabled && v2Style.point.color?.mode !== STYLE_MODE.FIXED) {
      result.colorLogicEnabled = true;
      result.colorLogicColumn = getColumnId(v2Style.point.color);
      result.colorLogicScheme = getColorScheme(v2Style.point.color);
      result.colorLogicType = v2Style.point.color.mode === 'category' ? 'category' : 'value';
      if (v2Style.point.color.mode === STYLE_MODE.RANGE) {
        result.colorLogicDataMin = v2Style.point.color.dataMin;
        result.colorLogicDataMax = v2Style.point.color.dataMax;
      }

      if (v2Style.point.color.mode === STYLE_MODE.CATEGORY) {
        result.customColors = v2Style.point.color.customColors;
      }
    }
  }

  return result;
}

export function parseStoredStyleData(mapboxStyleData: any): StyleState {
  try {
    let existingStyle: any;
    if (typeof mapboxStyleData === 'object' && mapboxStyleData !== null) {
      existingStyle = mapboxStyleData;
    } else {
      existingStyle = null;
    }

    if (isStyleV2(existingStyle)) {
      return parseV2StyleData(existingStyle as StyleV2);
    }

    const result: StyleState = {
      // Point/Circle styles - ensure all properties exist
      iconId: existingStyle?.icon?.value || DEFAULT_STYLE_VALUES.iconId,
      size:
        existingStyle?.circle?.['circle-radius'] ||
        existingStyle?.paint?.['circle-radius'] ||
        DEFAULT_STYLE_VALUES.size,
      sizeLogic: {
        sizeBy: existingStyle?.sizeLogic?.sizeBy ?? DEFAULT_STYLE_VALUES.sizeLogic.sizeBy,
        minSize: existingStyle?.sizeLogic?.minSize ?? DEFAULT_STYLE_VALUES.sizeLogic.minSize,
        maxSize: existingStyle?.sizeLogic?.maxSize ?? DEFAULT_STYLE_VALUES.sizeLogic.maxSize,
        direction: existingStyle?.sizeLogic?.direction ?? DEFAULT_STYLE_VALUES.sizeLogic.direction,
      },
      labelLogic: {
        labelBy: existingStyle?.labelLogic?.labelBy ?? DEFAULT_STYLE_VALUES.labelLogic.labelBy,
        labelText: existingStyle?.labelLogic?.labelText,
        size: existingStyle?.labelLogic?.size ?? DEFAULT_STYLE_VALUES.labelLogic.size,
        color: existingStyle?.labelLogic?.color ?? DEFAULT_STYLE_VALUES.labelLogic.color,
        haloColor:
          existingStyle?.labelLogic?.haloColor ?? DEFAULT_STYLE_VALUES.labelLogic.haloColor,
        haloWidth:
          existingStyle?.labelLogic?.haloWidth ?? DEFAULT_STYLE_VALUES.labelLogic.haloWidth,
        fontStyle:
          existingStyle?.labelLogic?.fontStyle ?? DEFAULT_STYLE_VALUES.labelLogic.fontStyle,
        textVariableAnchor:
          existingStyle?.labelLogic?.textVariableAnchor ??
          DEFAULT_STYLE_VALUES.labelLogic.textVariableAnchor,
        textRadialOffset:
          existingStyle?.labelLogic?.textRadialOffset ??
          DEFAULT_STYLE_VALUES.labelLogic.textRadialOffset,
      },
      fillColor: normalizeColor(
        existingStyle?.circle?.['circle-color'] ||
          existingStyle?.paint?.['circle-color'] ||
          DEFAULT_STYLE_VALUES.fillColor
      ),
      fillOpacity: toPercentage(
        existingStyle?.circle?.['circle-opacity'] ??
          existingStyle?.paint?.['circle-opacity'] ??
          toDecimal(DEFAULT_STYLE_VALUES.fillOpacity)
      ),
      strokeColor: normalizeColor(
        existingStyle?.circle?.['circle-stroke-color'] ||
          existingStyle?.paint?.['circle-stroke-color'] ||
          DEFAULT_STYLE_VALUES.strokeColor
      ),
      strokeWidth:
        existingStyle?.circle?.['circle-stroke-width'] ??
        existingStyle?.paint?.['circle-stroke-width'] ??
        DEFAULT_STYLE_VALUES.strokeWidth,
      strokeOpacity: toPercentage(
        existingStyle?.circle?.['circle-stroke-opacity'] ??
          existingStyle?.paint?.['circle-stroke-opacity'] ??
          toDecimal(DEFAULT_STYLE_VALUES.strokeOpacity)
      ),
      borderType: (() => {
        const lineStyle = existingStyle?.line;
        if (!lineStyle) return DEFAULT_STYLE_VALUES.borderType;

        const dashArray = lineStyle['line-dasharray'];
        const gapWidth = lineStyle['line-gap-width'];

        if (gapWidth === 2 && (!dashArray || (Array.isArray(dashArray) && dashArray.length === 0)))
          return 'double';

        if (!dashArray || (Array.isArray(dashArray) && dashArray.length === 0)) return 'solid';

        // Convert array to string for comparison with known patterns
        const dashArrayStr = JSON.stringify(dashArray);
        const dashedPatternStr = JSON.stringify(DASH_ARRAY_PATTERNS.DASHED);
        const dottedPatternStr = JSON.stringify(DASH_ARRAY_PATTERNS.DOTTED);

        if (dashArrayStr === dashedPatternStr) return 'dashed';
        if (dashArrayStr === dottedPatternStr) return 'dotted';

        return 'solid';
      })(),
      opacity: toPercentage(
        existingStyle?.circle?.['circle-opacity'] ??
          existingStyle?.paint?.['circle-opacity'] ??
          toDecimal(DEFAULT_STYLE_VALUES.opacity)
      ),

      // Polygon fill styles - ensure all properties exist
      polygonFillColor: normalizeColor(
        existingStyle?.fill?.['fill-color'] || DEFAULT_STYLE_VALUES.polygonFillColor
      ),
      polygonFillOpacity: toPercentage(
        existingStyle?.fill?.['fill-opacity'] ?? toDecimal(DEFAULT_STYLE_VALUES.polygonFillOpacity)
      ),

      // Polygon stroke styles
      polygonStrokeColor: normalizeColor(
        existingStyle?.line?.['line-color'] || DEFAULT_STYLE_VALUES.polygonStrokeColor
      ),
      polygonStrokeWidth:
        existingStyle?.line?.['line-width'] ?? DEFAULT_STYLE_VALUES.polygonStrokeWidth,
      polygonStrokeOpacity: toPercentage(
        existingStyle?.line?.['line-opacity'] ??
          toDecimal(DEFAULT_STYLE_VALUES.polygonStrokeOpacity)
      ),

      // Line styles - ensure all properties exist
      lineColor: normalizeColor(
        existingStyle?.line?.['line-color'] || DEFAULT_STYLE_VALUES.lineColor
      ),
      lineWidth: existingStyle?.line?.['line-width'] ?? DEFAULT_STYLE_VALUES.lineWidth,
      lineOpacity: toPercentage(
        existingStyle?.line?.['line-opacity'] ?? toDecimal(DEFAULT_STYLE_VALUES.lineOpacity)
      ),
      colorLogicColumn:
        existingStyle?.colorLogic?.colorLogicColumn || DEFAULT_STYLE_VALUES.colorLogicColumn,
      colorLogicScheme:
        existingStyle?.colorLogic?.colorLogicScheme || DEFAULT_STYLE_VALUES.colorLogicScheme,
      colorLogicType:
        existingStyle?.colorLogic?.colorLogicType || DEFAULT_STYLE_VALUES.colorLogicType,
      colorLogicEnabled:
        existingStyle?.colorLogic?.colorLogicEnabled || DEFAULT_STYLE_VALUES.colorLogicEnabled,
    };

    return result;
  } catch (e) {
    console.warn('Failed to parse stored style data:', e);
    // Always return complete defaults on any error
    return { ...DEFAULT_STYLE_VALUES };
  }
}

/**
 * Determines the current label mode based on labelStyle
 * Returns the LabelText mode if available, otherwise derives from columnId
 */
export function getCurrentLabelMode(labelStyle: LabelStyle | undefined): LabelModeValue {
  if (!labelStyle) return LABEL_MODE.NONE;

  const modeFromLabelText = labelStyle.labelText?.mode;
  const hasColumn = labelStyle.columnId !== null;

  if (modeFromLabelText) {
    return modeFromLabelText as LabelModeValue;
  }

  return hasColumn ? LABEL_MODE.SINGLE_COLUMN : LABEL_MODE.NONE;
}

/**
 * Calculates legend colors for a given scheme configuration
 * @returns Record mapping property values to their corresponding colors
 */
export function getLegendColorsForScheme({
  scheme,
  columnId = null,
  customColors = null,
  layer,
  colorLogicType,
  dataSources,
}: {
  scheme: string;
  columnId?: number | string | null;
  customColors?: CustomColorsConfig[] | null | undefined;
  layer: Layer;
  colorLogicType: 'category' | 'value';
  dataSources: DataSourceConfig[];
}): Record<string, string> {
  // Construct a minimal StyleState object with the new scheme to get its legend colors
  const styleStateForLegend: StyleState = {
    colorLogicEnabled: true,
    colorLogicScheme: scheme,
    colorLogicColumn: columnId as string | number | null,
    colorLogicType,
    customColors,
  } as StyleState;

  return getLayerLegendColors(styleStateForLegend, dataSources, layer);
}
